import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import { setRefetchFlag } from "@/store/slices/notificationSlice";
import useApiClient from "@/api/Client";
import ENDPOINTS from "@/api/endpoints";

/**
 * Custom hook to listen for notification changes across all pages
 * Automatically updates when new notifications arrive
 */
export const useNotificationRefresh = (onRefresh?: () => void) => {
  const dispatch = useDispatch();
  const { api } = useApiClient();
  const { shouldRefetch, unreadCount, lastReceivedNotification } = useSelector(
    (state: RootState) => state.notification,
  );

  // Callback when notification data needs to be refreshed
  const handleRefresh = useCallback(async () => {
    try {
      // Fetch updated unread count from backend
      await api.get(ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT);
      // Call custom callback if provided
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Error refreshing notifications:", error);
    }
  }, [api, onRefresh]);

  // Listen for shouldRefetch flag
  useEffect(() => {
    if (shouldRefetch) {
      console.log("useNotificationRefresh: Refetch triggered");
      handleRefresh();

      // Call custom callback if provided (e.g. for page-specific data fetching)
      if (onRefresh) {
        onRefresh();
      }

      dispatch(setRefetchFlag(false));
    }
  }, [shouldRefetch, dispatch, handleRefresh, onRefresh]);

  return {
    unreadCount,
    manualRefresh: handleRefresh,
    lastReceivedNotification,
  };
};
